import { useCallback, useMemo } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { DISPUTE_ACTIONS } from "@features/vessel-call/vessel-call-status/Operations/grid/OperationsDisputes.mapper";
import { useOperationsDocumentUpload } from "@features/vessel-call/vessel-call-status/Operations/hooks/useOperationsDocumentUpload";
import { useResolveDispute } from "@features/vessel-call/vessel-call-status/Operations/hooks/useResolveDispute";
import {
  getResolveDisputeDrawerSchema,
  RESOLVE_DISPUTE_DETAILS_MAX_LENGTH,
  RESOLVE_DISPUTE_DOCUMENT_CERTIFICATE_TYPE,
  RESOLVE_DISPUTE_DOCUMENT_FIELD,
  RESOLVE_DISPUTE_DOCUMENTS_MAX_FILES,
} from "@features/vessel-call/vessel-call-status/Operations/schema/resolveDisputeDrawerSchema";
import type { ResolveDisputeDrawerFormValues } from "@features/vessel-call/vessel-call-status/Operations/types/types";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DisputeDocument } from "@services/disputes/types";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

type ResolveDisputeDrawerProps = {
  disputeId: string | null;
  isOpen: boolean;
  onClose: () => void;
};

const LANG_BASE = "vessel-call.operations";
const TEST_ID_BASE = "operations-resolve-dispute";

const defaultValues: ResolveDisputeDrawerFormValues = {
  resolutionDetails: "",
  [RESOLVE_DISPUTE_DOCUMENT_FIELD]: [],
};

const ResolveDisputeDrawer = ({ disputeId, isOpen, onClose }: ResolveDisputeDrawerProps) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const uploadConditions = useFileUploadProfiles().noDisputeDocument;
  const schema = useMemo(() => getResolveDisputeDrawerSchema(t), [t]);
  const form = useForm<ResolveDisputeDrawerFormValues>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
    shouldUnregister: true,
    defaultValues,
  });

  const setFormDocuments = useCallback(
    (documents: DisputeDocument[]) => {
      form.setValue(RESOLVE_DISPUTE_DOCUMENT_FIELD, documents, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form],
  );
  const {
    files,
    isUploading,
    setFiles,
    handleUpload,
    validateFiles,
    validateDrop,
    syncDocumentsFromStore,
    resetDocuments,
  } = useOperationsDocumentUpload({
    fieldName: RESOLVE_DISPUTE_DOCUMENT_FIELD,
    certificateType: RESOLVE_DISPUTE_DOCUMENT_CERTIFICATE_TYPE,
    maxFiles: RESOLVE_DISPUTE_DOCUMENTS_MAX_FILES,
    onDocumentsChange: setFormDocuments,
  });
  const { resolveDispute, isResolvingDispute } = useResolveDispute();

  const allowedFileExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((extension) =>
        extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const handleClose = useCallback(() => {
    resetDocuments();
    form.reset(defaultValues);
    onClose();
  }, [form, onClose, resetDocuments]);

  const handleSubmit = () => {
    form.handleSubmit((values) => {
      if (isResolvingDispute) return;
      if (!disputeId) {
        notify({
          title: t("common.error"),
          description: t("common.something-went-wrong"),
          type: "error",
        });
        return;
      }

      resolveDispute(
        {
          disputeId,
          payload: {
            action: DISPUTE_ACTIONS.ACCEPT,
            disputeDetails: values.resolutionDetails.trim(),
            documents: values[RESOLVE_DISPUTE_DOCUMENT_FIELD] ?? [],
          },
        },
        { onSuccess: handleClose },
      );
    })();
  };

  return (
    <div data-testid={`${TEST_ID_BASE}-drawer`}>
      <FormProvider {...form}>
        <SideDrawer
          isChildDrawer
          isOpen={isOpen}
          onClose={handleClose}
          onCancel={handleClose}
          onSubmit={handleSubmit}
          isSubmitting={isResolvingDispute}
          isSubmitDisabled={isUploading || isResolvingDispute}
          keepMounted={false}
          title={t(`${LANG_BASE}.resolve-dispute-title`)}
          submitLabel={t(`${LANG_BASE}.resolve-dispute-button`)}
          cancelLabel={t("common.cancel")}
        >
          <div className="w-[400px] p-6 flex flex-col gap-6">
            <FormItem<ResolveDisputeDrawerFormValues, "resolutionDetails">
              name="resolutionDetails"
              type="textarea"
              label={t(`${LANG_BASE}.resolution-details`)}
              testId={`${TEST_ID_BASE}-details`}
              minRows={4}
              maxChars={RESOLVE_DISPUTE_DETAILS_MAX_LENGTH}
              showCharCounter
            />
            <div className="flex flex-col gap-4">
              <FormItem<ResolveDisputeDrawerFormValues, typeof RESOLVE_DISPUTE_DOCUMENT_FIELD>
                name={RESOLVE_DISPUTE_DOCUMENT_FIELD}
                type="file"
                label={t(`${LANG_BASE}.resolution-documents`)}
                testId={`${TEST_ID_BASE}-documents`}
                allowedFileExtensions={allowedFileExtensions}
                uploadConditions={uploadConditions}
                maxFiles={RESOLVE_DISPUTE_DOCUMENTS_MAX_FILES}
                files={files}
                setFiles={setFiles}
                uploadDescription={t("upload.additional-documents-upload", {
                  extensions: allowedFileExtensions.join(", "),
                  size: uploadConditions.maxSizeLabel,
                })}
                onUpload={handleUpload}
                fileValidation={validateFiles}
                validateDrop={validateDrop}
                disabled={isUploading}
              />
              {files.map((file, index) => (
                <SingleFile
                  key={`${file.name}-${index}`}
                  testId={`${TEST_ID_BASE}-document-${index}`}
                  file={file}
                  fieldName={RESOLVE_DISPUTE_DOCUMENT_FIELD}
                  currentFileIndex={index}
                  setFiles={() => {}}
                  removeFn={syncDocumentsFromStore}
                  isLoading={isUploading && file instanceof File}
                />
              ))}
            </div>
          </div>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default ResolveDisputeDrawer;
